/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

/**
 * Union of supported spinner sizes.
 *
 * - sm → 24px (h-6 w-6)
 * - md → 32px (h-8 w-8) [default]
 * - lg → 48px (h-12 w-12)
 */
export type SpinnerSize = 'sm' | 'md' | 'lg';

/**
 * Maps a SpinnerSize to its Tailwind size classes.
 * Internal helper; exported size values are documented by {@link SpinnerSize}.
 */
const spinnerMappings: Record<SpinnerSize, string> = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
};

/**
 * Props for {@link Spinner}.
 *
 * @property size - Visual size of the spinner. Defaults to 'md'.
 */
export interface SpinnerProps {
    size?: SpinnerSize;
}

/**
 * Accessible, theme-aware loading spinner built with SVG and Tailwind CSS.
 *
 * Contract
 * - Input: optional `size` ('sm' | 'md' | 'lg'); defaults to 'md'.
 * - Output: a <div role="status"> containing a spinning SVG and a visually hidden label.
 * - Errors: none; purely presentational.
 *
 * Accessibility
 * - Uses `role="status"` so assistive tech announces a live loading region.
 * - Includes a `.sr-only` text node ("Loading...") for screen readers.
 *
 * The spinner inherits color from the current text color and applies theme utilities
 * (`fill-theme-active` and `text-theme-input-border`). Size is controlled via Tailwind classes.
 *
 * Examples
 * ```tsx
 * <Spinner />
 * <Spinner size="sm" />
 * <Spinner size="lg" />
 * ```
 *
 * @param props - Component props.
 * @param props.size - Spinner size; one of 'sm' | 'md' | 'lg'. Defaults to 'md'.
 * @returns Spinner element.
 */
export default function Spinner(props: SpinnerProps) {
    return (
        <div className="mx-auto w-fit" role="status">
            <svg
                aria-hidden="true"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={
                    spinnerMappings[props.size ?? 'md'] + ' fill-theme-active text-theme-input-border animate-spin'
                }
            >
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                />
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    );
}
